package com.sweetlime.dungeonamigo.api

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

@Serializable
data class Open5eListResponse<T>(
    val count: Int = 0,
    val next: String? = null,
    val previous: String? = null,
    val results: List<T> = emptyList()
)

// ─── Shared nested types ───────────────────────────────────────────────────

@Serializable
data class SpeedDto(
    val walk: Int? = null,
    val swim: Int? = null,
    val fly: Int? = null,
    val burrow: Int? = null,
    val climb: Int? = null,
    val hover: Boolean? = null
)

@Serializable
data class ActionDto(
    val name: String = "",
    val desc: String = ""
)

// ─── DTOs ─────────────────────────────────────────────────────────────────

@Serializable
data class SpellDto(
    val slug: String = "",
    val name: String = "",
    val level: String? = null,
    val school: String? = null,
    @SerialName("casting_time") val castingTime: String? = null,
    val range: String? = null,
    val duration: String? = null,
    val components: String? = null,
    val material: String? = null,
    val desc: String? = null,
    @SerialName("higher_level") val higherLevel: String? = null,
    val dnd_class: String? = null,
    val ritual: String? = null
)

@Serializable
data class MonsterDto(
    val slug: String = "",
    val name: String = "",
    val size: String? = null,
    val type: String? = null,
    val subtype: String? = null,
    val alignment: String? = null,
    // Can be an Int or a list of {"type":"natural","value":17} objects
    @SerialName("armor_class") val armorClass: JsonElement? = null,
    @SerialName("armor_desc") val armorDesc: String? = null,
    @SerialName("hit_points") val hitPoints: Int? = null,
    @SerialName("hit_dice") val hitDice: String? = null,
    // Object with optional walk/swim/fly/burrow/climb keys
    val speed: SpeedDto? = null,
    @SerialName("challenge_rating") val challengeRating: String? = null,
    val desc: String? = null,
    val actions: List<ActionDto>? = null,
    @SerialName("special_abilities") val specialAbilities: List<ActionDto>? = null,
    val reactions: List<ActionDto>? = null,
    @SerialName("legendary_actions") val legendaryActions: List<ActionDto>? = null
) {
    fun armorClassValue(): Int? = when (armorClass) {
        null -> null
        is JsonPrimitive -> armorClass.intOrNull
        is JsonArray -> armorClass.jsonArray
            .firstOrNull()?.jsonObject?.get("value")?.jsonPrimitive?.intOrNull
        else -> null
    }
}

@Serializable
data class MagicItemDto(
    val slug: String = "",
    val name: String = "",
    val type: String? = null,
    val rarity: String? = null,
    @SerialName("requires_attunement") val requiresAttunement: String? = null,
    val desc: String? = null,
    val cost: String? = null,
    val weight: String? = null
)

@Serializable
data class WeaponDto(
    val slug: String = "",
    val name: String = "",
    val category: String? = null,
    val cost: String? = null,
    val weight: String? = null,
    @SerialName("damage_dice") val damageDice: String? = null,
    @SerialName("damage_type") val damageType: String? = null,
    val desc: String? = null
)

@Serializable
data class BackgroundDto(
    val slug: String = "",
    val name: String = "",
    val desc: String? = null
)

@Serializable
data class ClassDto(
    val slug: String = "",
    val name: String = "",
    @SerialName("hit_dice") val hitDice: String? = null,
    val desc: String? = null
)

@Serializable
data class RaceDto(
    val slug: String = "",
    val name: String = "",
    val desc: String? = null,
    val size: String? = null,
    // Same object shape as monsters
    val speed: SpeedDto? = null
)